/**
 * ArtifactCard.jsx — inline preview card rendered inside the assistant
 * message where a fenced ```html block would otherwise dump raw markup.
 *
 * Two states:
 *   1. Building (streaming) — soft cyan shimmer with a spinner + rotating
 *      "Composing layout…" → "Writing copy…" → "Adding hero imagery…"
 *      progress line so the user knows Rammy is actively working, not hung.
 *   2. Ready — a warm preview tile with the artifact title, a big Preview
 *      button that opens the Canvas side panel, AND (for HTML sites) a
 *      one-tap Share button that publishes the site and copies the public
 *      URL to the clipboard — no Canvas detour required.
 *
 * Non-technical users should NEVER see raw HTML in the chat feed; this
 * component is the entire user-facing surface for generated site output.
 */
import { useEffect, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LABELS = {
  html: { icon: "Globe",         title: "Website draft",   verb: "Building your website" },
  svg:  { icon: "Palette",       title: "Illustration",    verb: "Sketching your artwork" },
  code: { icon: "Code2",         title: "Code snippet",    verb: "Writing code" },
  markdown: { icon: "FileText",  title: "Document",        verb: "Drafting your doc" },
};

// Rotating human-readable progress steps per artifact kind. Emergent-style
// "here's what I'm doing right now" narration — makes a 30s wait feel
// intentional. UI-only; the actual work is one streaming LLM call, but the
// rotation matches the site-builder pipeline's real stages.
const STAGES_BY_KIND = {
  html: [
    "Composing layout & sections…",
    "Writing warm, on-brand copy…",
    "Choosing a colour palette…",
    "Generating custom hero imagery…",
    "Designing a brand logo…",
    "Polishing typography & spacing…",
    "Running a design self-critique…",
  ],
  svg: [
    "Sketching the composition…",
    "Choosing a colour palette…",
    "Rendering shapes & paths…",
    "Polishing the details…",
  ],
  code: [
    "Understanding what you need…",
    "Writing the implementation…",
    "Adding sensible defaults…",
    "Reviewing for edge cases…",
  ],
  markdown: [
    "Outlining the structure…",
    "Drafting the sections…",
    "Adding examples & context…",
    "Polishing the prose…",
  ],
};

/** Friendly card while the fenced block is still streaming. */
export function ArtifactBuildingCard({ lang, testid, onRetry, progressLabel }) {
  const meta = LABELS[normaliseKind(lang)] || LABELS.html;
  const Icon = L[meta.icon] || L.Sparkles;

  // Backend-driven stage narration overrides the UI-only rotation. When the
  // server emits `event: progress` frames from the site-builder stream, the
  // useChatStream hook lifts the latest label onto `progressLabel` and we
  // use it directly — no local rotation until we know something real. When
  // there's no progress frame yet (first ~2s of a build), the local
  // rotation gives immediate feedback so the card doesn't sit blank.
  const stages = STAGES_BY_KIND[normaliseKind(lang)] || STAGES_BY_KIND.html;
  const [stageIdx, setStageIdx] = useState(0);
  const STALL_MS = 90_000;
  const [showRetry, setShowRetry] = useState(false);
  useEffect(() => {
    const tick = setInterval(() => setStageIdx(i => (i + 1) % stages.length), 3500);
    const stall = setTimeout(() => setShowRetry(true), STALL_MS);
    return () => { clearInterval(tick); clearTimeout(stall); };
  }, []);
  const stageText = progressLabel || stages[stageIdx];

  return (
    <div
      className="my-3 rounded-2xl overflow-hidden border border-[var(--rammy-cyan)]/35 bg-gradient-to-br from-[var(--rammy-cyan)]/12 via-white to-[var(--rammy-cyan)]/6 w-full max-w-[520px] relative"
      data-testid={testid}
    >
      {/* Shimmer sweep */}
      <div
        aria-hidden
        className="absolute inset-0 -translate-x-full animate-[rammy-shimmer_2.2s_infinite] bg-gradient-to-r from-transparent via-white/50 to-transparent"
      />
      <div className="relative p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--rm-surface)] border border-[var(--rammy-cyan)]/30 flex items-center justify-center text-[var(--rammy-cyan-dark)] shrink-0">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">
            <L.Sparkles size={11} className="animate-pulse" />
            {meta.verb}…
          </div>
          <div className="mt-1 h-2.5 rounded-full bg-[var(--rammy-cyan)]/15 overflow-hidden">
            <div className="h-full w-2/3 bg-gradient-to-r from-[var(--rammy-cyan)] to-[var(--rammy-cyan-dark)] animate-pulse" />
          </div>
          <div
            className="mt-1.5 text-[11.5px] text-[var(--rammy-text-muted)] transition-opacity duration-300"
            key={stageText}
            data-testid={`${testid}-stage`}
          >
            {stageText}
          </div>
        </div>
        <L.Loader2 size={16} className="animate-spin text-[var(--rammy-cyan-dark)] shrink-0" />
      </div>
      {showRetry && onRetry && (
        <div className="relative border-t border-[var(--rammy-cyan)]/20 bg-[var(--rm-surface)]/50 px-4 py-2 flex items-center gap-2 text-[11.5px]">
          <L.AlertTriangle size={13} className="text-amber-600"/>
          <span className="flex-1 text-[var(--rammy-text-sec)]">Taking longer than usual — connection might have stalled.</span>
          <button
            type="button"
            onClick={onRetry}
            data-testid={`${testid}-retry`}
            className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--rammy-cyan-dark)] text-white text-[11.5px] font-semibold hover:opacity-90 transition"
          >
            <L.RotateCcw size={11}/> Retry
          </button>
        </div>
      )}
    </div>
  );
}

/** Card shown when a fenced artifact never got its closing ``` — i.e. the
 *  SSE stream was cut off mid-generation and the message was persisted with
 *  a half-written HTML/CSS block. Instead of leaking the raw opener into
 *  the chat, we surface a clear "was interrupted, retry?" affordance. */
export function ArtifactInterruptedCard({ lang, testid, onRetry }) {
  const meta = LABELS[normaliseKind(lang)] || LABELS.html;
  return (
    <div
      className="my-3 rounded-2xl overflow-hidden border border-amber-300 bg-amber-50/70 w-full max-w-[520px]"
      data-testid={testid}
    >
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--rm-surface)] border border-amber-300 flex items-center justify-center text-amber-700 shrink-0">
          <L.AlertTriangle size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-800">
            {meta.title} was interrupted
          </div>
          <div className="text-[13px] text-amber-950 leading-snug mt-0.5">
            Rammy got cut off before finishing this one. No code to read — just tap Retry to rebuild.
          </div>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            data-testid={`${testid}-retry`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-amber-800 text-white text-[12.5px] font-semibold hover:opacity-90 transition shrink-0"
          >
            <L.RotateCcw size={13} /> Retry
          </button>
        )}
      </div>
    </div>
  );
}


/** Finished-artifact preview card with a big Preview action and (for HTML
 *  sites only) a one-tap Share action that publishes the site and copies
 *  the public URL. Once published, we swap the button into a compact
 *  "Copied · rammy.site/…" affordance so the user immediately knows the
 *  link is on their clipboard. */
export function ArtifactReadyCard({ artifact, onOpen, testid }) {
  const meta = LABELS[artifact.kind] || LABELS.code;
  const Icon = L[meta.icon] || L.LayoutTemplate;
  const lines = (artifact.code || "").split("\n").length;
  const canShare = artifact.kind === "html";

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // { slug, public_url, title }

  const share = async () => {
    if (publishing) return;
    // Second click on the "Copied" state re-copies the link — friendlier
    // than making the user open Canvas just to grab it again.
    if (published?.public_url) {
      try {
        await navigator.clipboard.writeText(published.public_url);
        toast.success("Link copied");
      } catch { toast.error("Couldn't copy"); }
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post("/artifacts/publish", {
        html: artifact.code,
        title: prettyTitle(artifact),
      }, { timeout: 30000 });
      setPublished(data);
      try {
        await navigator.clipboard.writeText(data.public_url);
        toast.success("Published — link copied to clipboard");
      } catch {
        // Clipboard access can be blocked (permissions / iframe / preview).
        // The address bar + Open chip below still work, so guide the user
        // there honestly instead of pretending the tap will copy.
        toast.success("Published — use the Open link to visit your site");
      }
      // Publishing = strongest implicit approval → silent DNA learn.
      try {
        const plain = (artifact.code || "")
          .replace(/<script[\s\S]*?<\/script>/gi, "")
          .replace(/<style[\s\S]*?<\/style>/gi, "")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (plain.length >= 200) {
          api.post("/dna/learn", {
            sample: plain.slice(0, 12000),
            source: "published_artifact",
          }).catch(() => { /* silent */ });
        }
      } catch { /* never break the flow */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish. Try again");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="my-3 rounded-2xl overflow-hidden border border-[var(--rammy-cyan)]/40 bg-[var(--rm-surface)] rammy-shadow w-full max-w-[520px] group/artifact"
      data-testid={testid}
    >
      {/* Faux browser chrome — communicates "this is a live site" without
          exposing anything technical. Once published, the address bar swaps
          to the REAL slug so the tile doubles as a visual confirmation. */}
      <div className="h-8 bg-gradient-to-b from-[var(--rammy-cyan)]/15 to-[var(--rammy-cyan)]/5 border-b border-[var(--rammy-cyan)]/25 flex items-center gap-1.5 px-3">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-300/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-300/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-300/70" />
        <div className="ml-2 h-4 rounded-md bg-[var(--rm-surface)]/70 border border-[var(--rammy-cyan)]/20 flex-1 max-w-[260px] flex items-center gap-1 px-1.5">
          <L.Lock size={8} className="text-[var(--rammy-text-muted)]" />
          {published?.public_url ? (
            <a
              href={published.public_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-mono text-[var(--rammy-cyan-dark)] truncate hover:underline"
              data-testid={`${testid}-public-url`}
            >
              {stripProtocol(published.public_url)}
            </a>
          ) : (
            <span className="text-[10px] font-mono text-[var(--rammy-text-muted)] truncate">
              rammy.site/{slugify(artifact.title)}
            </span>
          )}
        </div>
      </div>

      <div className="p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--rammy-cyan)]/25 to-[var(--rammy-cyan)]/8 border border-[var(--rammy-cyan)]/30 flex items-center justify-center text-[var(--rammy-cyan-dark)] shrink-0">
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] flex items-center gap-1">
            <L.Check size={11} /> {published ? "Published · link copied" : `${meta.title} ready`}
          </div>
          <div className="text-[15px] font-medium text-[var(--rammy-text)] leading-snug truncate">
            {prettyTitle(artifact)}
          </div>
          <div className="text-[11.5px] text-[var(--rammy-text-muted)] mt-0.5">
            {lines.toLocaleString()} lines · designed &amp; imaged by Rammy
          </div>
        </div>
      </div>

      {/* Actions — Preview always visible; Share only for HTML sites since
          SVG/markdown/code aren't publishable "sites". */}
      <div className="px-4 pb-4 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`${testid}-preview`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--rammy-cyan-dark)] text-white text-[12.5px] font-semibold hover:opacity-90 transition"
          title="Open in Canvas"
        >
          <L.Eye size={14} /> Preview
        </button>
        {canShare && (
          <button
            type="button"
            onClick={share}
            disabled={publishing}
            data-testid={`${testid}-share`}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full border text-[12.5px] font-semibold transition disabled:opacity-70 ${
              published
                ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[var(--rammy-text)] hover:border-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-cyan-dark)]"
            }`}
            title={published ? "Copy the shareable link again" : "Publish and copy the shareable link"}
          >
            {publishing ? (
              <><L.Loader2 size={14} className="animate-spin" /> Publishing…</>
            ) : published ? (
              <><L.Check size={14} /> Link copied</>
            ) : (
              <><L.Share2 size={14} /> Share</>
            )}
          </button>
        )}
        {published?.public_url && (
          <a
            href={published.public_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-open-url`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-cyan-dark)] transition"
            title="Open the published site in a new tab"
          >
            <L.ExternalLink size={13} /> Open
          </a>
        )}
      </div>
    </div>
  );
}

function normaliseKind(lang) {
  const l = (lang || "").toLowerCase();
  if (["html", "htm"].includes(l)) return "html";
  if (l === "svg") return "svg";
  if (["md", "markdown"].includes(l)) return "markdown";
  return "html"; // Default assumes site builder — most common streaming case
}

function prettyTitle(art) {
  // Prefer a real title extracted from the artifact itself when possible.
  if (art.kind === "html") {
    // <title> is the strongest signal — a designer's chosen page name.
    const tt = /<title[^>]*>([^<]{1,80})<\/title>/i.exec(art.code || "");
    if (tt && tt[1].trim()) return tt[1].trim();
    // Fall back to the first <h1> if <title> is absent or empty.
    const h1 = /<h1[^>]*>([\s\S]{1,80}?)<\/h1>/i.exec(art.code || "");
    if (h1 && h1[1]) {
      const clean = h1[1].replace(/<[^>]+>/g, "").trim();
      if (clean) return clean;
    }
    return "Your new landing page";
  }
  if (art.kind === "svg") return "Custom illustration";
  if (art.kind === "markdown") return "Document draft";
  return (art.title || "").trim() || "Snippet";
}

function slugify(s) {
  return (s || "site").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 24) || "site";
}

function stripProtocol(url) {
  return (url || "").replace(/^https?:\/\//, "");
}
